import type { ReactNode } from "react";
import { cn } from "../lib/cn";

/**
 * NumberedSteps. Vertical list of editorial steps numbered 01, 02, 03.
 * Each row carries the mono index + a prose line, with optional italic
 * emphasis and bold accent inline. Used by ScreenCloser ("what to expect
 * next"), any onboarding outro, and future "here's how this works"
 * explainers.
 *
 * Steps can be passed as plain strings (rendered as-is) or as React
 * nodes (so callers can include their own <em>/<b> emphasis inline).
 */
export interface NumberedStep {
  text: ReactNode;
}

export interface NumberedStepsProps {
  steps: ReadonlyArray<NumberedStep>;
  className?: string;
}

export function NumberedSteps({ steps, className }: NumberedStepsProps) {
  return (
    <ol className={cn("m-0 flex list-none flex-col gap-4 p-0", className)}>
      {steps.map((step, i) => (
        <li
          // biome-ignore lint/suspicious/noArrayIndexKey: numbered steps are a caller-supplied stable sequence
          key={`step-${i}`}
          className="flex gap-4 border-b border-mist pb-4 last:border-b-0 last:pb-0"
        >
          <span className="shrink-0 font-mono text-xs font-semibold uppercase tracking-[0.16em] text-teal-700">
            {(i + 1).toString().padStart(2, "0")}
          </span>
          <p className="m-0 max-w-[60ch] font-sans text-lg leading-[1.55] text-navy [&_b]:font-medium [&_b]:text-navy [&_em]:not-italic [&_em]:font-medium [&_em]:text-teal-800">
            {step.text}
          </p>
        </li>
      ))}
    </ol>
  );
}
